import React from 'react';
import { Select, Tag, Space, Button, Input, Row, Col } from 'antd';
import {
  UserOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  SearchOutlined,
} from '@ant-design/icons';

const { Option } = Select;

// 筛选值
export interface FilterValues {
  orgBuyers?: string[];
  orgAgents?: string[];
  orgBidders?: string[];
  riskPoints?: string[];
  strategyKeywords?: string[];
  topics?: string[];
  laws?: string[];
  replyPositions?: string[];
  keyword?: string;
}

interface TagFilterProps {
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  onSearch: () => void;
  onReset: () => void;
}

// 预设选项
const presetOptions = {
  orgBuyers: ['云南烟叶复烤有限责任公司', '崇左某单位', '昆明市政府采购中心'],
  orgAgents: ['广西同泽工程项目管理股份有限公司', '云南招标股份有限公司'],
  orgBidders: ['合肥科大立安安全技术有限责任公司', '广西裕达农副产品配送有限公司'],
  riskPoints: [
    '超越资质范围',
    '排他性条款',
    '低于成本价',
    '参数歧义',
    '业绩要求',
    '付款方式',
    '工期要求',
  ],
  strategyKeywords: ['维持原判', '澄清说明', '修改招标文件', '部分采纳', '完全采纳'],
  topics: ['资格条件', '技术参数', '报价评审', '程序流程', '中标结果', '人员要求'],
  laws: ['中华人民共和国招标投标法', '中华人民共和国建筑法', '评标委员会和评标方法暂行规定'],
  replyPositions: ['不采纳', '部分采纳', '完全采纳', '澄清说明'],
};

// 所有筛选器平铺配置
const allFilters = [
  { key: 'orgBuyers', label: '招标人', color: 'blue', options: presetOptions.orgBuyers, icon: <UserOutlined /> },
  { key: 'orgAgents', label: '代理机构', color: 'purple', options: presetOptions.orgAgents },
  { key: 'orgBidders', label: '投标人', color: 'cyan', options: presetOptions.orgBidders },
  { key: 'riskPoints', label: '风险点', color: 'red', options: presetOptions.riskPoints, icon: <WarningOutlined /> },
  { key: 'topics', label: '主题', color: 'orange', options: presetOptions.topics },
  { key: 'strategyKeywords', label: '策略', color: 'green', options: presetOptions.strategyKeywords },
  { key: 'laws', label: '法规', color: 'magenta', options: presetOptions.laws },
  { key: 'replyPositions', label: '结论', color: 'blue', options: presetOptions.replyPositions, icon: <CheckCircleOutlined /> },
];

const TagFilter: React.FC<TagFilterProps> = ({ values, onChange, onSearch, onReset }) => {
  const handleChange = (key: keyof FilterValues, selected: string[]) => {
    onChange({ ...values, [key]: selected });
  };

  const hasActiveFilters = Object.entries(values).some(
    ([key, val]) => key !== 'keyword' && val && Array.isArray(val) && val.length > 0
  );

  const renderSelect = (
    key: keyof FilterValues,
    label: string,
    options: string[],
    color: string
  ) => (
    <Select
      mode="multiple"
      placeholder={label}
      value={values[key] as string[]}
      onChange={(selected) => handleChange(key, selected)}
      className="w-full"
      maxTagCount={0}
      maxTagPlaceholder={(omitted) => `+${omitted.length}`}
      allowClear
     
      popupMatchSelectWidth={false}
    >
      {options.map((opt) => (
        <Option key={opt} value={opt}>
          <Tag color={color} className="mr-0 text-xs">
            {opt.length > 12 ? opt.slice(0, 12) + '...' : opt}
          </Tag>
        </Option>
      ))}
    </Select>
  );

  return (
    <div className="p-4">
      {/* 第一行：关键词搜索 + 标签筛选横向并列 */}
      <Row gutter={[16, 12]} align="middle" className="mb-3">
        <Col xs={24} lg={6}>
          <Input.Search
            placeholder="关键词全文检索（项目名称、质疑内容、答复理由等）"
            value={values.keyword}
            onChange={(e) => onChange({ ...values, keyword: e.target.value })}
            onSearch={onSearch}
            allowClear
           
            prefix={<SearchOutlined className="text-gray-400" />}
          />
        </Col>
        {allFilters.slice(0, 4).map((filter) => (
          <Col xs={12} sm={8} md={6} lg={4} key={filter.key}>
            {renderSelect(
              filter.key as keyof FilterValues,
              filter.label,
              filter.options,
              filter.color
            )}
          </Col>
        ))}
      </Row>

      {/* 第二行：更多筛选条件 */}
      <Row gutter={[16, 12]} align="middle">
        {allFilters.slice(4).map((filter) => (
          <Col xs={12} sm={8} md={6} lg={4} key={filter.key}>
            {renderSelect(
              filter.key as keyof FilterValues,
              filter.label,
              filter.options,
              filter.color
            )}
          </Col>
        ))}
        <Col xs={12} sm={8} md={6} lg={4}>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
              搜索
            </Button>
            {hasActiveFilters && (
              <Button icon={<ClearOutlined />} onClick={onReset}>
                清空
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* 已选标签汇总 - 放到顶部 */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">已选条件:</span>
            {Object.entries(values).map(([key, val]) => {
              if (!val || !Array.isArray(val) || val.length === 0) return null;
              const filter = allFilters.find((f) => f.key === key);
              return val.map((v) => (
                <Tag
                  key={`${key}-${v}`}
                  closable
                 
                  color={filter?.color}
                  onClose={() =>
                    handleChange(
                      key as keyof FilterValues,
                      val.filter((x) => x !== v)
                    )
                  }
                  className="text-xs"
                >
                  {filter?.label}: {v.length > 10 ? v.slice(0, 10) + '...' : v}
                </Tag>
              ));
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagFilter;
